import { useQueryClient, useIsFetching, type QueryKey } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface RefreshButtonProps {
  /** Query key prefix to invalidate, e.g. ['accounts']. Prefix-matches all param variants. */
  queryKey: QueryKey
  /** Plural entity name for the accessible label, e.g. "accounts". */
  label: string
}

/**
 * Manual refresh control for list pages. Invalidates the page's TanStack Query
 * (prefix-matching every params variant) to re-pull the current list, and spins
 * the icon / disables itself while any matching query is in flight.
 */
export function RefreshButton({ queryKey, label }: RefreshButtonProps) {
  const queryClient = useQueryClient()
  const isFetching = useIsFetching({ queryKey }) > 0

  return (
    <Button
      variant="outline"
      onClick={() => queryClient.invalidateQueries({ queryKey })}
      disabled={isFetching}
      aria-label={`Refresh ${label}`}
      title="Refresh"
    >
      <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} aria-hidden="true" />
      Refresh
    </Button>
  )
}
